import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "list", "spinner"]
  static values  = { url: String }

  connect() {
    this._loading = false
    this._exhausted = false
    this._cursor = null
    this._lastDateLabel = null
    this._count = 0
  }

  open() {
    this._count = 0
    this._cursor = null
    this._exhausted = false
    this._lastDateLabel = null
    this.listTarget.innerHTML = ""
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    this._loadMore()
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  backdropClose(e) {
    if (e.target === e.currentTarget) this.close()
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  onScroll(e) {
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      this._loadMore()
    }
  }

  closeOnEscape(e) {
    if (e.key === "Escape" && !this.modalTarget.classList.contains("hidden")) this.close()
  }

  // ── Private ──────────────────────────────────────────────────────

  async _loadMore() {
    if (this._loading || this._exhausted) return
    this._loading = true
    this.spinnerTarget.classList.remove("hidden")

    let url = this.urlValue
    if (this._cursor) {
      url += (url.includes("?") ? "&" : "?") + `before=${encodeURIComponent(this._cursor)}`
    }

    try {
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      const data = await resp.json()
      const entries = data.entries || []
      const nextCursor = data.next_cursor

      if (entries.length === 0) {
        this._exhausted = true
        this.spinnerTarget.classList.add("hidden")
        if (this._count === 0) {
          this.listTarget.innerHTML = `
            <div class="flex flex-col items-center justify-center py-12 text-center">
              <svg class="w-8 h-8 text-gray-300 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>
              <p class="text-[13px] text-gray-400">No activity recorded.</p>
            </div>`
        }
        this._loading = false
        return
      }

      this._count += entries.length
      this._cursor = nextCursor

      if (!nextCursor || entries.length < 20) {
        this._exhausted = true
      }

      entries.forEach(entry => {
        // Insert date header if new date group
        if (entry.date_label !== this._lastDateLabel) {
          this._lastDateLabel = entry.date_label
          const header = document.createElement("div")
          header.className = "flex items-center gap-3 px-6 pt-5 pb-1"
          header.innerHTML = `
            <span class="text-[13px] font-medium text-gray-400 whitespace-nowrap">${this._esc(entry.date_label)}</span>
            <div class="flex-1 border-t border-gray-100"></div>`
          this.listTarget.appendChild(header)
        }

        // Timeline entry
        const row = document.createElement("a")
        row.href = entry.path
        row.className = "group relative flex items-start gap-3 py-0.5 px-6 pl-[43px] hover:bg-gray-50 transition-colors"
        row.innerHTML = `
          <div class="absolute left-[27px] top-0 bottom-0 w-px bg-gray-200"></div>
          <div class="relative z-10 flex-shrink-0 w-[22px] h-[22px] flex items-center justify-center bg-white">
            ${entry.icon_svg}
          </div>
          <div class="flex-1 min-w-0 flex items-baseline justify-between gap-3">
            <p class="text-[13px] text-gray-500 truncate">
              ${this._esc(entry.user_name)} ${this._esc(entry.verb)} ${this._esc(entry.entity)}
            </p>
            <span class="text-[13px] text-gray-400 whitespace-nowrap flex-shrink-0 tabular-nums">
              ${this._esc(entry.timestamp)}
            </span>
          </div>`
        this.listTarget.appendChild(row)
      })
    } catch (e) {
      console.error("Activity feed load error:", e)
    }

    this.spinnerTarget.classList.add("hidden")
    this._loading = false

    // If content doesn't overflow yet, keep loading to fill the viewport
    if (!this._exhausted) {
      const scrollEl = this.listTarget.parentElement
      requestAnimationFrame(() => {
        if (scrollEl.scrollHeight <= scrollEl.clientHeight) {
          this._loadMore()
        }
      })
    }
  }

  _esc(str) {
    const div = document.createElement("div")
    div.textContent = str || ""
    return div.innerHTML
  }
}
